import { defineStore } from 'pinia';
import axios from 'axios';
import { messageTypes, styleTypes } from '@/utils/notifications';
import OsUtil from '@/utils/os';
import { UserService } from '@/services/user';
import { useNotificationsStore } from '@/stores/notifications';
import { useHubUiStore } from '@/stores/hubUi';

/** State properties living directly on the root store (module states not included) */
interface AppState {
  /** State of the navigation drawer (legacy; only set via mutation dispatched elsewhere) */
  navigationDrawer: boolean | null;
  /** Whether the connection to the backend is lost. `null` = not yet determined */
  connectionLost: boolean | null;
  /** Version of the currently running app bundle, e.g. "1.2.3" */
  appVersion: string;
  /** Version reported by the server's version.txt, or `false` if not fetched yet */
  originAppVersion: string | false;
}

/**
 * Pinia conversion of the legacy ROOT vuex store state/getters/mutations/actions in
 * `src/store/index.ts` (store id `'app'`; there is no legacy namespace prefix for these — they
 * were committed/dispatched with no `/`). Not persisted (the legacy root store had no
 * `vuexPersist`/reducer entry of its own).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Getters `globalSnackbar`, `connectionLost`, `appVersion` were pure identity passthroughs of
 *   the state prop of the same name. Pinia exposes state props directly on the store instance
 *   (`useAppStore().globalSnackbar`), so these getters are DROPPED entirely rather than renamed —
 *   consumers read the state prop directly, which is exactly what the old getter returned.
 * - Legacy root ACTION `connectionLost` collides with the `connectionLost` state prop (Vuex kept
 *   getters/mutations/actions in separate registries; Pinia has one). Renamed to
 *   `handleConnectionLost`.
 */
export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    navigationDrawer: null, // state of the navigation drawer
    connectionLost: null,
    appVersion: import.meta.env.VITE_VERSION || '1.0.0',
    originAppVersion: false,
  }),
  getters: {
    appVersionUptodate(state): boolean {
      if (state.originAppVersion) {
        return state.originAppVersion === state.appVersion;
      }

      return true;
    },
  },
  actions: {
    setConnectionLost(lost: boolean) {
      this.connectionLost = lost;
    },
    /**
     * Legacy root snackbar entry point. Old-hub iframe pages still commit
     * `addSnackbarMessage` through the legacy bridge (`commitLegacyMutation`); the
     * `globalSnackbar` state it used to write was never rendered in the new hub, so
     * forward to the alert system that actually is (SnackbarStack via hubUi).
     */
    addSnackbarMessage(message: any) {
      const text = typeof message === 'string' ? message : message?.message;
      if (!text) return;
      const allowed = ['info', 'error', 'success', 'warning'] as const;
      const type = allowed.includes(message?.type) ? message.type : 'info';
      useHubUiStore().addAlert({ message: text, type });
    },
    setOriginAppVersion(version: string) {
      this.originAppVersion = version;
    },
    setNavigationDrawer({ show }: { show: boolean }) {
      // NOTE: the legacy action committed a mutation named `setNavigationDrawer` that was never
      // actually defined anywhere in `src/store/index.ts` (only `setConnectionLost`,
      // `addSnackbarMessage`, and `setOriginAppVersion` mutations existed there) — so in vuex this
      // commit silently no-op'd (unknown mutation type warning) and `navigationDrawer` was never
      // actually set. Nothing in the codebase currently calls `setNavigationDrawer` (grepped
      // `src/`), so there's no observable behavior to preserve either way. Per the mechanical
      // "mutation becomes an action that mutates `this` directly" conversion rule, this inlines
      // the assignment the missing mutation would have made, which is strictly more correct than
      // reproducing the historical no-op. Flagged as a deviation in phase1-report.md.
      this.navigationDrawer = show;
    },
    /**
     * Renamed from legacy root action `connectionLost` — see the collision note in this store's
     * top-level doc comment and RENAMES.md.
     */
    async handleConnectionLost() {
      // Double check if connection is really lost
      const { data } = await UserService.getUser();

      if (data) {
        this.connectionLost = false;
        return;
      }

      this.connectionLost = true;

      const name = 'app-connection-lost';
      const notificationsStore = useNotificationsStore();

      const notification = {
        name,
        messageType: messageTypes.NO_CONNECTION,
        styleType: styleTypes.ERROR,
        action: function () {
          setTimeout(() => {
            // Legacy `forceReload` argument (not in current DOM typings), kept for identical runtime behavior
            (window.location as any).reload(true);
          }, 500);
        },
        actionSecondary: () => {
          notificationsStore.removeNotification(name);
        },
      };

      notificationsStore.addNotification(notification);
    },
    checkVersion() {
      const currentTimestamp = new Date().getTime();
      axios
        .get(`${import.meta.env.VITE_HUB_URL}/version.txt?ts=${currentTimestamp}`)
        .then((response) => {
          this.originAppVersion = response.data;

          const queryString = window.location.search;
          const urlParams = new URLSearchParams(queryString);
          const isSecondTry = urlParams.has('newVersion');

          if (!this.appVersionUptodate) {
            // Versioned name: `stayDismissed` is persisted, so a plain name would hide the
            // banner for every later release too. The retry (error) banner is dismissed separately.
            const version = String(response.data).trim();
            useNotificationsStore().addNotification({
              name: `new-version-${version}${isSecondTry ? '-retry' : ''}`,
              messageType: !isSecondTry
                ? messageTypes.NEW_VERSION
                : OsUtil.getOs() === 'windows'
                  ? messageTypes.NEW_VERSION_PC
                  : messageTypes.NEW_VERSION_MAC,
              styleType: !isSecondTry ? styleTypes.WARNING : styleTypes.ERROR,
              stayDismissed: true,
              action: () => {
                const reloadUrl = new URL(
                  `${window.location.origin}${window.location.pathname}`,
                );
                reloadUrl.searchParams.set('newVersion', 'true');
                reloadUrl.searchParams.set('ts', Date.now().toString());

                // Fetch index.html with cache: 'reload' to bust stale browser cache
                // (old nginx config served index.html with max-age=30d, so some
                // browsers still hold a cached copy that ignores server updates)
                fetch(reloadUrl.toString(), { cache: 'reload' })
                  .finally(() => {
                    window.location.replace(reloadUrl.toString());
                  });
              },
            });
          }
        })
        .catch(() => {
          // do nothing
        });
    },
  },
});
